<template>
  <div>
    <!-- Page Header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <div class="d-flex flex-wrap align-items-center justify-content-between">
          <div class="mb-2 mb-md-0">
            <h1 class="h4 mb-1 font-weight-bold">Income receipts</h1>
            <p class="text-muted mb-0">Record and track money you've received from customers</p>
          </div>
          <div class="d-flex align-items-center">
            <CButton
              color="light"
              class="mr-2"
              aria-label="Refresh"
              title="Refresh"
              :disabled="loading"
              @click="refreshAll"
            >
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Create receipt
            </CButton>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- Receipt Insights -->
    <CRow class="mb-4">
      <CCol lg="8" class="mb-4 mb-lg-0">
        <CCard class="h-100">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h2 class="h6 font-weight-bold mb-1">Receipt summary</h2>
            <div class="small text-muted">All receipts, with the last 6 months below</div>
          </CCardHeader>
          <CCardBody class="pt-0 pb-4 px-4">
            <CRow class="mb-4">
              <CCol sm="6" md="3" class="mb-3 mb-md-0">
                <div class="border rounded p-3 h-100">
                  <div class="small font-weight-bold text-muted mb-2">Total receipts</div>
                  <div class="h4 font-weight-bold mb-0">{{ receiptSummary.totalCount }}</div>
                  <div class="small text-muted mt-1">All time</div>
                </div>
              </CCol>
              <CCol sm="6" md="3" class="mb-3 mb-md-0">
                <div class="border rounded p-3 h-100">
                  <div class="small font-weight-bold text-muted mb-2">Received</div>
                  <div class="h4 font-weight-bold mb-0">RM {{ formatMyr(receiptSummary.monthAmount) }}</div>
                  <div class="small text-muted mt-1">This month</div>
                </div>
              </CCol>
              <CCol sm="6" md="3" class="mb-3 mb-md-0">
                <div class="border rounded p-3 h-100">
                  <div class="small font-weight-bold text-muted mb-2">Average amount</div>
                  <div class="h4 font-weight-bold mb-0">RM {{ formatMyr(receiptSummary.averageAmount) }}</div>
                  <div class="small text-muted mt-1">Per receipt, all time</div>
                </div>
              </CCol>
              <CCol sm="6" md="3">
                <div class="border rounded p-3 h-100">
                  <div class="small font-weight-bold text-muted mb-2">Top customer</div>
                  <div class="h5 font-weight-bold mb-0 text-truncate">
                    {{ receiptSummary.topCustomer || "None yet" }}
                  </div>
                  <div class="small text-muted mt-1">
                    {{ receiptSummary.topCustomer ? `RM ${formatMyr(receiptSummary.topCustomerAmount)}, all time` : "All time" }}
                  </div>
                </div>
              </CCol>
            </CRow>
            <div style="height: 250px">
              <ReceiptTrendChart
                v-if="allReceipts.length"
                :chartData="receiptTrendData"
              />
              <div
                v-else
                class="d-flex align-items-center justify-content-center h-100 text-muted"
              >
                <p class="mb-0">The chart appears after your first receipt.</p>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <!-- Top customers -->
      <CCol lg="4">
        <CCard class="h-100">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h2 class="h6 font-weight-bold mb-1">Top customers</h2>
            <div class="small text-muted">By amount received, all time</div>
          </CCardHeader>
          <CCardBody class="pt-0 pb-4 px-4">
            <div v-if="topCustomers.length">
              <div
                v-for="customer in topCustomers"
                :key="customer.name"
                class="d-flex align-items-center justify-content-between mb-3"
              >
                <div class="text-truncate mr-2">
                  <div class="font-weight-medium text-truncate">{{ customer.name }}</div>
                  <div class="small text-muted">
                    {{ customer.receiptCount }} receipt{{ customer.receiptCount === 1 ? "" : "s" }}
                  </div>
                </div>
                <div class="text-right">
                  <div class="font-weight-bold">RM {{ formatMyr(customer.totalAmount) }}</div>
                  <div class="small text-muted">{{ customer.percentage }}%</div>
                </div>
              </div>
            </div>
            <p v-else class="text-muted mb-0 py-4 text-center">
              Your top customers appear here once you record receipts.
            </p>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Receipt List -->
    <CCard class="mb-4">
      <CCardHeader class="bg-transparent border-0 p-4">
        <h2 class="h6 font-weight-bold mb-0">
          All receipts
          <CBadge color="light" class="ml-2">{{ totalItems }}</CBadge>
        </h2>
      </CCardHeader>
      <CCardBody class="p-4">
        <!-- Filters -->
        <CCollapse :show="showFilters">
          <CCard class="mb-4 border">
            <CCardBody>
              <CRow>
                <CCol md="3">
                  <CInput
                    label="Receipt number"
                    v-model="filters.receiptNumber"
                    placeholder="Search receipt number"
                    @keyup.enter="applyFilters"
                  />
                </CCol>
                <CCol md="3">
                  <CInput
                    label="Customer"
                    v-model="filters.customerName"
                    placeholder="Search customer"
                    @keyup.enter="applyFilters"
                  />
                </CCol>
                <CCol md="3">
                  <CInput
                    type="date"
                    label="From"
                    v-model="filters.dateFrom"
                    @change="applyFilters"
                  />
                </CCol>
                <CCol md="3">
                  <CInput
                    type="date"
                    label="To"
                    v-model="filters.dateTo"
                    :is-valid="dateRangeInvalid ? false : null"
                    invalid-feedback="The To date must be on or after the From date."
                    @change="applyFilters"
                  />
                </CCol>
              </CRow>
              <CRow class="mt-3">
                <CCol class="d-flex justify-content-end">
                  <CButton color="light" class="mr-2" @click="resetFilters">
                    <CIcon name="cil-x" /> Clear filters
                  </CButton>
                  <CButton color="primary" @click="applyFilters">
                    <CIcon name="cil-filter" /> Apply filters
                  </CButton>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCollapse>

        <div class="d-flex flex-wrap align-items-center mb-4">
          <CButton color="light" @click="showFilters = !showFilters">
            <CIcon :name="showFilters ? 'cil-chevron-top' : 'cil-filter'" />
            {{ showFilters ? "Hide filters" : "Show filters" }}
          </CButton>
          <CBadge v-if="hasActiveFilters" color="primary" class="ml-2">
            {{ activeFiltersCount }} active
          </CBadge>
          <CButton v-if="hasActiveFilters" color="link" class="ml-2" @click="resetFilters">
            Clear filters
          </CButton>
        </div>

        <!-- Loading (first load) -->
        <div v-if="loading && !items.length" class="text-center py-5">
          <CSpinner color="primary" />
          <p class="mt-3 text-muted mb-0">Loading receipts…</p>
        </div>

        <!-- Error -->
        <div v-else-if="loadError" class="text-center py-5">
          <CIcon name="cil-warning" size="2xl" class="mb-3 text-danger" />
          <h3 class="h6 font-weight-bold">Couldn't load receipts</h3>
          <p class="text-muted mb-3">Check your connection and try again.</p>
          <CButton color="primary" @click="refreshTable">
            <CIcon name="cil-reload" /> Try again
          </CButton>
        </div>

        <!-- Empty (first use) -->
        <div v-else-if="!items.length && !hasActiveFilters" class="text-center py-5">
          <CIcon name="cil-description" size="2xl" class="mb-3 text-muted" />
          <h3 class="h6 font-weight-bold">No income receipts yet</h3>
          <p class="text-muted mb-3">
            Give customers a receipt each time they pay you.
          </p>
          <CButton color="primary" @click="addNew">
            <CIcon name="cil-plus" /> Create receipt
          </CButton>
        </div>

        <!-- Empty (filtered) -->
        <div v-else-if="!items.length" class="text-center py-4">
          <h3 class="h6 font-weight-bold">No receipts match your filters</h3>
          <CButton color="light" class="mt-2" @click="resetFilters">
            <CIcon name="cil-x" /> Clear filters
          </CButton>
        </div>

        <template v-else>
          <div v-if="loading" class="progress mb-3" style="height: 4px">
            <div class="progress-bar progress-bar-striped progress-bar-animated w-100 bg-primary"></div>
          </div>

          <!-- Desktop table -->
          <div class="d-none d-lg-block">
            <CDataTable
              :items="computedItems"
              :fields="fields"
              :sorter="{ external: false }"
              :table-filter="false"
              :items-per-page-select="{
                values: [10, 25, 50, 100],
                label: 'Items per page:',
              }"
              :items-per-page="perPage"
              hover
              striped
              bordered
              responsive
              @pagination-change="onPaginationChange"
            >
              <template #show_index="{ index }">
                <td class="py-2 text-muted">
                  {{ (currentPage - 1) * perPage + index + 1 }}
                </td>
              </template>

              <template #receiptNumber="{ item }">
                <td class="py-2">
                  <router-link
                    :to="`/tenants/incomeReceipt/${item.id}`"
                    class="font-weight-bold text-monospace"
                  >
                    {{ item.receiptNumber }}
                  </router-link>
                </td>
              </template>

              <template #customerName="{ item }">
                <td class="py-2">{{ item.customerName }}</td>
              </template>

              <template #displayIssuedDate="{ item }">
                <td class="py-2">{{ item.displayIssuedDate }}</td>
              </template>

              <template #grandTotal="{ item }">
                <td class="py-2 text-right">
                  <span class="font-weight-bold">RM {{ formatMyr(item.grandTotal) }}</span>
                </td>
              </template>

              <template #show_details="{ item }">
                <td class="py-2 position-relative">
                  <div class="text-right action-buttons">
                    <CButtonGroup>
                      <CButton
                        color="light"
                        size="sm"
                        class="border"
                        :aria-label="`Edit receipt ${item.receiptNumber}`"
                        title="Edit"
                        @click="onEdit(item)"
                      >
                        <CIcon name="cil-pencil" />
                      </CButton>
                      <CDropdown
                        size="sm"
                        color="light"
                        placement="bottom-end"
                        class="dropdown-with-zindex border"
                      >
                        <template #toggler-content>
                          <CIcon name="cil-options" />
                          <span class="sr-only">More actions for {{ item.receiptNumber }}</span>
                        </template>
                        <CDropdownItem @click="onDuplicate(item)">
                          <CIcon name="cil-copy" class="mr-2" /> Duplicate
                        </CDropdownItem>
                        <CDropdownItem @click="downloadPDF(item)">
                          <CIcon name="cil-cloud-download" class="mr-2" /> Download PDF
                        </CDropdownItem>
                        <CDropdownDivider />
                        <CDropdownItem class="text-danger" @click="showDeleteConfirmation(item)">
                          <CIcon name="cil-trash" class="mr-2" /> Delete
                        </CDropdownItem>
                      </CDropdown>
                    </CButtonGroup>
                  </div>
                </td>
              </template>
            </CDataTable>
          </div>

          <!-- Mobile card list -->
          <div class="d-lg-none">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <CSelect v-model="perPage" class="mb-0" @change="onMobilePerPage">
                <option :value="10">10 per page</option>
                <option :value="25">25 per page</option>
                <option :value="50">50 per page</option>
              </CSelect>
              <span class="text-muted small">
                {{ paginationStart }}–{{ paginationEnd }} of {{ totalItems }}
              </span>
            </div>

            <CCard v-for="item in computedItems" :key="item.id" class="mb-3">
              <CCardBody class="py-3">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <router-link
                      :to="`/tenants/incomeReceipt/${item.id}`"
                      class="font-weight-bold text-monospace"
                    >
                      {{ item.receiptNumber }}
                    </router-link>
                    <div class="small text-muted">{{ item.displayIssuedDate }}</div>
                  </div>
                  <div class="font-weight-bold text-right">RM {{ formatMyr(item.grandTotal) }}</div>
                </div>
                <div>{{ item.customerName }}</div>
              </CCardBody>
              <CCardFooter class="py-2 d-flex justify-content-between bg-transparent">
                <CButton color="light" class="border" @click="onEdit(item)">
                  <CIcon name="cil-pencil" /> Edit
                </CButton>
                <CDropdown color="light" placement="bottom-end" class="dropdown-mobile border">
                  <template #toggler-content>
                    <CIcon name="cil-options" /> More
                  </template>
                  <CDropdownItem @click="onDuplicate(item)">
                    <CIcon name="cil-copy" class="mr-2" /> Duplicate
                  </CDropdownItem>
                  <CDropdownItem @click="downloadPDF(item)">
                    <CIcon name="cil-cloud-download" class="mr-2" /> Download PDF
                  </CDropdownItem>
                  <CDropdownDivider />
                  <CDropdownItem class="text-danger" @click="showDeleteConfirmation(item)">
                    <CIcon name="cil-trash" class="mr-2" /> Delete
                  </CDropdownItem>
                </CDropdown>
              </CCardFooter>
            </CCard>
          </div>

          <!-- Pagination (server side) -->
          <div class="d-flex flex-wrap align-items-center justify-content-between mt-3">
            <small class="text-muted d-none d-lg-inline">
              {{ paginationStart }}–{{ paginationEnd }} of {{ totalItems }} receipts
            </small>
            <CPagination
              v-if="totalPages > 1"
              :activePage="currentPage"
              :pages="totalPages"
              size="sm"
              class="mb-0"
              @update:activePage="onPageChange"
            />
          </div>
        </template>
      </CCardBody>
    </CCard>

    <!-- Delete confirmation -->
    <CModal
      :title="`Delete receipt ${itemToDelete.receiptNumber || ''}?`"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
      @update:show="onDeleteConfirmation"
    >
      <p class="mb-0">
        The receipt for <strong>{{ itemToDelete.customerName }}</strong>
        (RM {{ formatMyr(itemToDelete.grandTotal || 0) }}) will be removed.
        This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">
          Keep it
        </CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          <CSpinner v-if="deleting" size="sm" class="mr-1" />
          Delete receipt
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import IncomeReceiptApi from "@/lib/incomeReceiptApi";
import ReceiptTrendChart from "../widgets/ReceiptTrendChart";
import { formatMyr } from "@/utils/money";
import { formatDate, asDateString, mytToday } from "@/utils/datetime";
import { badgeChartColor } from "@/utils/statusBadges";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Last n calendar months in Malaysia time, oldest first: [{ key: "2026-10", label: "Oct" }]
function lastMonths(n) {
  const [y, m] = mytToday().split("-").map(Number);
  const out = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(y, m - 1 - i, 1));
    out.push({ key: d.toISOString().slice(0, 7), label: MONTH_LABELS[d.getUTCMonth()] });
  }
  return out;
}

const monthOf = (dt) => asDateString(dt).slice(0, 7);

export default {
  name: "IncomeReceiptList",
  components: {
    ReceiptTrendChart,
  },
  data() {
    return {
      loading: true,
      loadError: false,
      deleting: false,
      items: [],
      fields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:50px",
          sorter: false,
          filter: false,
        },
        {
          key: "receiptNumber",
          label: "Receipt no.",
          sorter: true,
          filter: true,
        },
        {
          key: "customerName",
          label: "Customer",
          sorter: true,
          filter: true,
        },
        {
          key: "displayIssuedDate",
          label: "Issued",
          sorter: true,
          filter: true,
        },
        {
          key: "grandTotal",
          label: "Amount",
          sorter: true,
          filter: false,
          _style: "text-align: right",
        },
        {
          key: "show_details",
          label: "Actions",
          _style: "width:110px",
          sorter: false,
          filter: false,
        },
      ],
      api: new IncomeReceiptApi(),
      warningModal: false,
      itemToDelete: {},
      currentPage: 1,
      perPage: 10,
      totalItems: 0,
      totalPages: 1,
      showFilters: false,
      filters: {
        receiptNumber: "",
        customerName: "",
        dateFrom: "",
        dateTo: "",
      },
      // All receipts, for the summary (real data)
      allReceipts: [],
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Sales", "Receipts"]);
    this.refreshTable();
    this.fetchSummary();
  },
  computed: {
    computedItems() {
      return this.items.map((item) => {
        return {
          ...item,
          displayIssuedDate: formatDate(item.issuedDate),
          receiptNumber: item.incomeReceiptNumber,
          customerName: this.getCustomerName(item),
          grandTotal: this.getGrandTotal(item),
        };
      });
    },
    paginationStart() {
      if (this.totalItems === 0) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },
    paginationEnd() {
      return Math.min(this.currentPage * this.perPage, this.totalItems);
    },
    hasActiveFilters() {
      return Object.values(this.filters).some((filter) => filter !== "");
    },
    activeFiltersCount() {
      return Object.values(this.filters).filter((filter) => filter !== "").length;
    },
    dateRangeInvalid() {
      return !!(this.filters.dateFrom && this.filters.dateTo && this.filters.dateTo < this.filters.dateFrom);
    },
    receiptSummary() {
      const all = this.allReceipts || [];
      const thisMonth = monthOf(mytToday());
      let totalAmount = 0;
      let monthAmount = 0;
      all.forEach((r) => {
        const amt = this.getGrandTotal(r);
        totalAmount += amt;
        if (monthOf(r.issuedDate) === thisMonth) monthAmount += amt;
      });
      const top = this.topCustomers[0];
      return {
        totalCount: all.length,
        monthAmount,
        averageAmount: all.length ? totalAmount / all.length : 0,
        topCustomer: top ? top.name : "",
        topCustomerAmount: top ? top.totalAmount : 0,
      };
    },
    topCustomers() {
      const all = this.allReceipts || [];
      const byCustomer = {};
      let grand = 0;
      all.forEach((r) => {
        const name = this.getCustomerName(r);
        const amt = this.getGrandTotal(r);
        grand += amt;
        if (!byCustomer[name]) byCustomer[name] = { name, totalAmount: 0, receiptCount: 0 };
        byCustomer[name].totalAmount += amt;
        byCustomer[name].receiptCount += 1;
      });
      return Object.values(byCustomer)
        .sort((a, b) => b.totalAmount - a.totalAmount)
        .slice(0, 5)
        .map((c) => ({ ...c, percentage: grand ? Math.round((c.totalAmount / grand) * 1000) / 10 : 0 }));
    },
    receiptTrendData() {
      const months = lastMonths(6);
      const all = this.allReceipts || [];
      const color = badgeChartColor("incomeReceipt", "Accepted"); // success
      return {
        labels: months.map((m) => m.label),
        datasets: [
          {
            label: "Received",
            backgroundColor: "transparent",
            borderColor: color,
            pointBackgroundColor: color,
            data: months.map((m) =>
              all.filter((r) => monthOf(r.issuedDate) === m.key).reduce((a, r) => a + this.getGrandTotal(r), 0)
            ),
          },
        ],
      };
    },
  },
  methods: {
    formatMyr,
    getCustomerName(item) {
      if (item.customer && item.customer.name) return item.customer.name;
      return "No customer";
    },
    getGrandTotal(incomeReceipt) {
      var total = 0;
      if (incomeReceipt.items && incomeReceipt.items.length > 0) {
        for (var i = 0; i < incomeReceipt.items.length; i++) {
          var item = incomeReceipt.items[i];
          total += (item.price || 0) * (item.quantity || 0);
        }
      }
      return total;
    },
    fetchSummary() {
      this.api
        .getListByCurrentBusiness({ page: 1, pageSize: 1000 })
        .then((r) => {
          this.allReceipts = (r && ((r.result && (r.result.data || r.result)) || r.data)) || [];
          if (!Array.isArray(this.allReceipts)) this.allReceipts = [];
        })
        .catch(() => { /* summary degrades gracefully; the table still works */ });
    },
    refreshAll() {
      this.refreshTable();
      this.fetchSummary();
    },
    refreshTable() {
      var self = this;
      self.loading = true;
      self.loadError = false;

      const params = {
        page: self.currentPage,
        pageSize: self.perPage,
      };
      if (self.filters.receiptNumber) params.receiptNumber = self.filters.receiptNumber;
      if (self.filters.customerName) params.customerName = self.filters.customerName;
      if (self.filters.dateFrom) params.dateFrom = self.filters.dateFrom;
      if (self.filters.dateTo) params.dateTo = self.filters.dateTo;

      self.api
        .getListByCurrentBusiness(params)
        .then((response) => {
          if (response && response.result) {
            self.items = response.result.data || [];
            self.totalItems = response.result.totalRecords || 0;
            self.currentPage = response.result.page || 1;
            self.totalPages = response.result.totalPages || Math.ceil(self.totalItems / self.perPage);
          } else {
            self.items = response || [];
            self.totalItems = self.items.length;
            self.totalPages = Math.ceil(self.totalItems / self.perPage);
          }
          if (self.totalItems > 0 && self.totalPages < 1) {
            self.totalPages = 1;
          }
        })
        .catch((error) => {
          console.error("Failed to load income receipts", error);
          self.loadError = true;
        })
        .finally(() => {
          self.loading = false;
        });
    },
    // CDataTable emits the new page size here
    onPaginationChange(itemsPerPage) {
      const size = parseInt(itemsPerPage);
      if (size && size !== this.perPage) {
        this.perPage = size;
        this.currentPage = 1;
        this.refreshTable();
      }
    },
    onMobilePerPage(evt) {
      const size = parseInt(evt && evt.target ? evt.target.value : evt);
      if (size) {
        this.perPage = size;
        this.currentPage = 1;
        this.refreshTable();
      }
    },
    onPageChange(page) {
      if (page !== this.currentPage) {
        this.currentPage = page;
        this.refreshTable();
      }
    },
    onEdit(item) {
      this.$router.push({ path: `/tenants/incomeReceipt/${item.id}` });
    },
    onDuplicate(item) {
      this.$router.push({ path: `/tenants/incomeReceipt/${item.id}/duplicate` });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    confirmDelete() {
      if (this.deleting) return;
      this.deleting = true;
      const number = this.itemToDelete.receiptNumber;
      this.api
        .delete(this.itemToDelete.id)
        .then(() => {
          this.warningModal = false;
          this.refreshAll();
          this.$toast.success(number ? `Receipt ${number} deleted` : "Receipt deleted");
        })
        .catch(({ data } = {}) => {
          this.$toast.error(`Couldn't delete the receipt. ${helper.getErrorMessage(data)}`);
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    onDeleteConfirmation(status) {
      if (!status) {
        this.itemToDelete = {};
      }
    },
    addNew() {
      this.$router.push({ path: "/tenants/incomeReceipt" });
    },
    resetFilters() {
      this.filters = {
        receiptNumber: "",
        customerName: "",
        dateFrom: "",
        dateTo: "",
      };
      this.currentPage = 1;
      this.refreshTable();
    },
    applyFilters() {
      if (this.dateRangeInvalid) return;
      this.currentPage = 1;
      this.refreshTable();
    },
    downloadPDF(item) {
      const filename = `${item.incomeReceiptNumber || "receipt"}.pdf`;
      this.api.downloadPDF(item.id, filename).catch(() => {
        this.$toast.error("Couldn't download the PDF. Check your connection and try again.");
      });
    },
  },
};
</script>

<style scoped>
/* Keep the row's dropdown above neighbouring rows */
.dropdown-with-zindex {
  position: static;
}

.dropdown-with-zindex .dropdown-menu {
  z-index: 1050 !important;
  position: absolute;
  will-change: transform;
}

.dropdown-mobile .dropdown-menu {
  z-index: 1055 !important;
  position: absolute;
  will-change: transform;
}

.action-buttons,
.action-buttons .btn-group {
  position: relative;
  z-index: auto;
}

.action-buttons:has(.dropdown.show),
.action-buttons .btn-group:has(.dropdown.show) {
  z-index: 1060;
}

::v-deep .table tbody td { vertical-align: middle; }
</style>
